import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, Observable, retry, take, timer } from 'rxjs';
import {
  CreateSessionBody,
  CreateSessionDrillBody,
  CreateSessionResponse,
  CreateSessionRunBody,
  SessionsService
} from '@session/infra';
import { RunDraft, SessionDraft, SessionDrillDraft, SessionStore } from '../state';

const STALE_MS: number = 30_000;
const MAX_RETRIES: number = 3;
const BASE_DELAY_MS: number = 500;

/**
 * Resumes a pending session POST on app boot when localStorage holds a
 * draft that the previous run flagged as pendingSubmit. Fire-and-forget:
 * we never block UI on this, and a 409 clears the draft (plan deleted
 * server-side, retry would never succeed).
 */
@Injectable({ providedIn: 'root' })
export class SessionSyncService {
  private readonly store: SessionStore = inject(SessionStore);
  private readonly sessionsService: SessionsService = inject(SessionsService);

  attemptResume(): void {
    const draft: SessionDraft | null = this.store.snapshot();
    if (draft === null) return;
    if (!draft.pendingSubmit) return;
    if (!this.isStale(draft.lastSubmitAttemptAt)) return;
    this.submit(draft).pipe(take(1)).subscribe();
  }

  private submit(draft: SessionDraft): Observable<CreateSessionResponse | never> {
    this.store.markSubmitPending();
    const body: CreateSessionBody = this.buildBody(draft);
    return this.sessionsService.create(body).pipe(
      retry({
        count: MAX_RETRIES,
        delay: (error: unknown, retryCount: number) => {
          if (error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500) {
            // 4xx (incl. 409) → don't retry; let outer catchError decide
            throw error;
          }
          return timer(BASE_DELAY_MS * Math.pow(2, retryCount - 1));
        }
      }),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 409) {
          // plan deleted server-side - drop the draft, nothing to retry
          this.store.clear();
        } else {
          // network / 5xx exhausted retries - leave draft as pendingSubmit
          this.store.clearSubmitPending();
        }
        return EMPTY;
      })
      // success path: clear the draft entirely
      // (matches save-session.command-handler success behaviour)
    );
  }

  private buildBody(draft: SessionDraft): CreateSessionBody {
    return {
      clientGeneratedId: draft.clientGeneratedId,
      startedAt: draft.startedAt,
      planId: draft.planId,
      sessionDrills: draft.sessionDrills.map(
        (sd: SessionDrillDraft): CreateSessionDrillBody => ({
          drillId: sd.drillId,
          drillName: sd.drillName,
          scoring: sd.scoring,
          shotsCount: sd.shotsCount,
          targetRuns: sd.targetRuns,
          runs: sd.runs.map(
            (r: RunDraft): CreateSessionRunBody => ({
              recordedAt: r.recordedAt,
              reps: r.reps,
              timeSeconds: r.timeSeconds,
              parTimeSeconds: r.parTimeSeconds,
              hitsA: r.hitsA,
              hitsC: r.hitsC,
              hitsD: r.hitsD,
              hitsM: r.hitsM,
              hitsNS: r.hitsNS
            })
          )
        })
      )
    };
  }

  private isStale(lastAttemptAt: string | null): boolean {
    if (lastAttemptAt === null) return true;
    const last: number = Date.parse(lastAttemptAt);
    if (!Number.isFinite(last)) return true;
    return Date.now() - last > STALE_MS;
  }
}
